import { Icon } from '@iconify/react'
import { a, config, useSpring } from '@react-spring/web'
import { GradientTexture, GradientType, Html } from '@react-three/drei'
import { CuboidCollider, RigidBody } from '@react-three/rapier'
import { Suspense, useRef } from 'react'
import { MathUtils, Mesh } from 'three'

import { Bulldozer, Crane, Sign } from '@components/models'
import { randomInt } from '@utils'

import { TitleText } from './TitleText'

export function World() {
  const title = 'DAMMAFRA'
  const letterSpacing = 0.8

  const firstLetterRef = useRef<Mesh>(null!)

  const spring = useSpring({ from: { opacity: 0 }, to: { opacity: 1 }, config: config.molasses })

  return (
    <>
      <Suspense>
        <Crane
          position={[-3.9, 0, -1.25]}
          rotation-y={MathUtils.degToRad(-43)}
          onMove={position => {
            if (!firstLetterRef.current) return
            const newPosition = position.clone()
            newPosition.x -= 0.4
            newPosition.y -= 1.05
            newPosition.z -= 0.15
            firstLetterRef.current.position.copy(newPosition)
          }}
        />
        <TitleText ref={firstLetterRef} position={[-title.length * 0.5 * letterSpacing, 0, 0]}>
          {title.charAt(0)}
        </TitleText>
      </Suspense>

      <Suspense>
        {title
          .substring(1, title.length - 1)
          .split('')
          .map((c, i, string) => (
            <RigidBody
              key={c + i}
              position={[(i - string.length * 0.5) * letterSpacing, randomInt(5, 8), 0]}
            >
              <TitleText>{c}</TitleText>
            </RigidBody>
          ))}
      </Suspense>

      <Suspense>
        <Bulldozer position={[6, 0, -3.2]} rotation-y={MathUtils.degToRad(43)} />
        <RigidBody
          position={[(title.length - 1 - title.length * 0.5) * letterSpacing + 1, 0, -1]}
          lockRotations
        >
          <TitleText>{title.charAt(title.length - 1)}</TitleText>
        </RigidBody>
      </Suspense>

      <Suspense>
        <Sign position={[2, 0, 5]} />
        <Sign position={[-2, 0, 5]} rotation-y={MathUtils.degToRad(-25)} />
      </Suspense>

      <Html
        transform
        rotation-x={MathUtils.degToRad(-90)}
        position-z={3}
        scale={0.5}
        zIndexRange={[9, 0]}
      >
        <a.div
          className="h-44 flex flex-col justify-between items-center text-center scale-200"
          style={spring}
        >
          <div>
            <h1 className="text-2xl text-stroke-white">Francesco Dammacco</h1>
            <h2 className="text-stroke-white">Creative Developer</h2>
          </div>

          <a
            className="no-underline text-sm border p-1 bg-sky-700 text-white hover:bg-white active:bg-white hover:text-sky-700 active:text-sky-700 uppercase"
            target="_blank"
            href="https://linktr.ee/dammafra"
          >
            Projects
          </a>

          <div className="flex ga-4">
            <a target="_blank" href="https://www.x.com/dammafra/">
              <Icon
                icon="ri:twitter-x-fill"
                style={{ stroke: 'white', strokeWidth: '0.5px' }}
                className="size-6 hover:text-white active:text-white"
              />
            </a>
            <a target="_blank" href="https://www.linkedin.com/in/dammafra/">
              <Icon
                icon="mdi:linkedin"
                style={{ stroke: 'white', strokeWidth: '0.5px' }}
                className="size-6 hover:text-white active:text-white"
              />
            </a>
            <a target="_blank" href="https://github.com/dammafra/">
              <Icon
                icon="mdi:github"
                style={{ stroke: 'white', strokeWidth: '0.5px' }}
                className="size-6 hover:text-white active:text-white"
              />
            </a>
            <a target="_blank" href="mailto:dammafra@outlook.it">
              <Icon
                icon="mdi:email"
                style={{ stroke: 'white', strokeWidth: '0.5px' }}
                className="size-6 hover:text-white active:text-white"
              />
            </a>
          </div>

          <p className="text-[9px] text-black border border-dashed p-1 bg-amber-300 w-fit uppercase">
            Under Construction
          </p>
        </a.div>
      </Html>

      <mesh rotation-x={MathUtils.degToRad(-90)} scale={5} position-y={-0.001}>
        <circleGeometry />
        <meshBasicMaterial transparent>
          <GradientTexture
            stops={[0, 1]}
            colors={['#fff085', '#efb100']}
            size={512}
            width={512}
            type={GradientType.Radial}
            innerCircleRadius={0}
            outerCircleRadius={200}
          />
        </meshBasicMaterial>
      </mesh>

      <RigidBody type="fixed" colliders={false} restitution={0.1} position={[0, -0.1, 0]}>
        <CuboidCollider args={[10, 0.1, 10]} />
      </RigidBody>
    </>
  )
}
